import { useEffect, useRef, useState, useCallback } from 'react';
import Markdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import SwarmVisualization from '../components/SwarmVisualization';
import { templates } from '../data/templates';
import { api } from '../api/client';

type SwarmStatus = 'idle' | 'running' | 'paused' | 'stopped' | 'error';

interface AgentStatus {
  id: string;
  name: string;
  color: string;
  status: 'active' | 'idle' | 'error';
  tasksCompleted: number;
}

interface WebResult {
  title: string;
  url: string;
  snippet: string;
}

interface AgentOutput {
  agent: string;
  type: string;
  message: string;
  web_results: WebResult[];
  model: string;
}

interface OrchestrationResult {
  reply: string;
  footnotes: { agent: string; type: string; note: string }[];
  agents_used: string[];
  agent_count: number;
  web_results: WebResult[];
  agent_outputs: AgentOutput[];
}

interface SavedOrchestration {
  _id: string;
  prompt: string;
  reply: string;
  agents_used: string[];
  created_at: string;
}

export default function SwarmDashboard() {
  const logRef = useRef<HTMLDivElement>(null);
  const [swarmStatus, setSwarmStatus] = useState<SwarmStatus>('idle');
  const [selectedTemplate, setSelectedTemplate] = useState(templates[0]);
  const [, setSelectedNode] = useState<{ name: string; role: string; color: string } | null>(null);
  const [taskInput, setTaskInput] = useState('');
  const [currentTask, setCurrentTask] = useState('');
  const [logs, setLogs] = useState<string[]>([
    '[system] Swarm initialized',
    '[system] Waiting for start signal...',
  ]);
  const [progress, setProgress] = useState({ data: 0, processing: 0, output: 0 });
  const [agentStatuses, setAgentStatuses] = useState<AgentStatus[]>([]);
  const [showOutput, setShowOutput] = useState(false);
  const [result, setResult] = useState<OrchestrationResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [history, setHistory] = useState<SavedOrchestration[]>([]);
  const [showHistory, setShowHistory] = useState(false);
  const [loadingHistory, setLoadingHistory] = useState(false);

  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Load search history
  const loadHistory = useCallback(async () => {
    setLoadingHistory(true);
    try {
      const res = await api.get('/api/v1/orchestrations?limit=20');
      setHistory(res.data.results || []);
    } catch {
      // silent
    }
    setLoadingHistory(false);
  }, []);

  useEffect(() => {
    loadHistory();
  }, [loadHistory]);

  useEffect(() => {
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, []);

  // Initialize agent statuses
  useEffect(() => {
    setAgentStatuses(
      selectedTemplate.agents.map((a) => ({
        id: a.id,
        name: a.name,
        color: a.color,
        status: 'idle' as const,
        tasksCompleted: 0,
      }))
    );
  }, [selectedTemplate]);

  const addLog = (msg: string) => {
    setLogs((prev) => [...prev, `[${new Date().toLocaleTimeString()}] ${msg}`]);
  };

  // Simulate progress while waiting for API
  const startProgressSimulation = () => {
    let elapsed = 0;
    intervalRef.current = setInterval(() => {
      elapsed += 500;
      const ratio = Math.min(elapsed / 30000, 1); // 30s max
      setProgress({
        data: Math.min(100, Math.round(ratio * 100)),
        processing: Math.max(0, Math.min(100, Math.round((ratio * 150) - 50))),
        output: Math.max(0, Math.min(100, Math.round((ratio * 200) - 100))),
      });

      // Animate agent statuses
      const activeCount = Math.min(selectedTemplate.agents.length, Math.floor(ratio * selectedTemplate.agents.length) + 1);
      setAgentStatuses((prev) =>
        prev.map((a, i) => ({
          ...a,
          status: i < activeCount ? 'active' : 'idle',
          tasksCompleted: i < activeCount ? Math.floor(ratio * 8) + Math.floor(Math.random() * 3) : 0,
        }))
      );
    }, 500);
  };

  const stopProgressSimulation = () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  };

  const handleStart = async () => {
    const task = taskInput.trim();
    if (!task) return;

    resetSimulation(true);
    setCurrentTask(task);
    setSwarmStatus('running');
    setResult(null);
    setError(null);

    setLogs([
      `[system] Swarm started with ${selectedTemplate.name}`,
      `[system] Task: ${task}`,
      `[system] Dispatching to ${selectedTemplate.agents.length} agents...`,
    ]);

    startProgressSimulation();

    try {
      addLog('[system] Searching the web...');

      const response = await api.post('/api/v1/orchestrate', {
        prompt: task,
      });

      stopProgressSimulation();

      const data: OrchestrationResult = response.data;
      setResult(data);
      setSwarmStatus('idle');
      setShowOutput(true);

      // Add real agent output to logs
      if (data.agent_outputs) {
        data.agent_outputs.forEach((ao) => {
          addLog(`[${ao.agent}] Completed — ${ao.message.substring(0, 100)}...`);
        });
      }
      addLog(`[system] Swarm complete — ${data.agents_used?.length || 0} agents, ${data.web_results?.length || 0} web sources`);

      // Set agent statuses to completed
      setAgentStatuses((prev) =>
        prev.map((a) => ({
          ...a,
          status: 'idle',
          tasksCompleted: Math.floor(Math.random() * 8) + 2,
        }))
      );

      setProgress({ data: 100, processing: 100, output: 100 });

      // Reload history
      loadHistory();
    } catch (err: unknown) {
      stopProgressSimulation();
      setSwarmStatus('error');
      const msg = err instanceof Error ? err.message : 'Unknown error';
      setError(msg);
      addLog(`[error] Swarm failed: ${msg}`);
      setAgentStatuses((prev) =>
        prev.map((a) => ({ ...a, status: 'error' }))
      );
    }
  };

  const handlePause = () => {
    setSwarmStatus('paused');
    addLog('[system] Swarm paused');
  };

  const handleStop = () => {
    stopProgressSimulation();
    setSwarmStatus('stopped');
    addLog('[system] Swarm stopped');
    setAgentStatuses((prev) =>
      prev.map((a) => ({ ...a, status: 'idle' }))
    );
  };

  const resetSimulation = (keepTaskInput = false) => {
    stopProgressSimulation();
    setProgress({ data: 0, processing: 0, output: 0 });
    setShowOutput(false);
    setResult(null);
    setError(null);
    if (!keepTaskInput) {
      setTaskInput('');
      setCurrentTask('');
    }
  };

  const handleNodeClick = (node: { id: string; name: string; role: string; color: string }) => {
    setSelectedNode(node);
    addLog(`Inspecting agent: ${node.name}`);
  };

  const statusColors: Record<SwarmStatus, string> = {
    idle: 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400',
    running: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
    paused: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400',
    stopped: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
    error: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400',
  };

  const taskEmpty = taskInput.trim().length === 0;

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-800 dark:text-gray-100">Swarm Dashboard</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            Research competitors, create content, monitor websites, and answer questions — all with teams of private AI agents working together.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={() => { setShowHistory(!showHistory); if (!showHistory) loadHistory(); }}
            className="px-3 py-1.5 text-xs rounded-lg font-medium bg-gray-100 text-gray-600 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-400 dark:hover:bg-gray-600"
          >
            {showHistory ? 'Hide' : 'History'}
          </button>
          <span className={`px-3 py-1 text-xs rounded-full font-medium ${statusColors[swarmStatus]}`}>
            {swarmStatus === 'error' ? 'Error' : swarmStatus.charAt(0).toUpperCase() + swarmStatus.slice(1)}
          </span>
          {swarmStatus !== 'running' && swarmStatus !== 'paused' && (
            <button
              onClick={handleStart}
              disabled={taskEmpty}
              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-sm font-medium"
            >
              ▶ Start
            </button>
          )}
          {swarmStatus === 'running' && (
            <button
              onClick={handlePause}
              className="px-4 py-2 bg-yellow-500 text-white rounded-lg hover:bg-yellow-600 text-sm font-medium"
            >
              ⏸ Pause
            </button>
          )}
          {swarmStatus !== 'idle' && swarmStatus !== 'error' && (
            <button
              onClick={handleStop}
              className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 text-sm font-medium"
            >
              ⏹ Stop
            </button>
          )}
        </div>
      </div>

      {/* Search History */}
      {showHistory && (
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-4">
          <h3 className="font-semibold text-gray-800 dark:text-gray-100 mb-3">Recent Searches</h3>
          {loadingHistory ? (
            <p className="text-sm text-gray-400">Loading...</p>
          ) : history.length === 0 ? (
            <p className="text-sm text-gray-400">No past searches yet.</p>
          ) : (
            <div className="space-y-2 max-h-60 overflow-y-auto">
              {history.map((h) => (
                <button
                  key={h._id}
                  onClick={() => {
                    setTaskInput(h.prompt);
                    setShowHistory(false);
                  }}
                  className="w-full text-left p-3 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 border border-gray-100 dark:border-gray-700 transition-colors"
                >
                  <p className="text-sm font-medium text-gray-800 dark:text-gray-200 truncate">{h.prompt}</p>
                  <p className="text-xs text-gray-400 mt-1">
                    {new Date(h.created_at).toLocaleString()} — {h.agents_used?.length || 0} agents
                  </p>
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Task Input */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-4">
        <label htmlFor="swarm-task" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          What should the swarm research?
        </label>
        <div className="flex gap-2">
          <input
            id="swarm-task"
            type="text"
            value={taskInput}
            onChange={(e) => setTaskInput(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && !taskEmpty && swarmStatus !== 'running' && handleStart()}
            placeholder="e.g., Research laundromats in zip code 06902"
            disabled={swarmStatus === 'running'}
            className="flex-1 px-4 py-2.5 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent disabled:opacity-60"
          />
        </div>
      </div>

      {/* Template Selector */}
      <div className="flex gap-2 flex-wrap">
        {templates.map((t) => (
          <button
            key={t.id}
            onClick={() => {
              setSelectedTemplate(t);
              setSwarmStatus('idle');
            }}
            className={`px-3 py-1.5 text-xs rounded-lg font-medium transition-colors ${
              selectedTemplate.id === t.id
                ? 'bg-purple-600 text-white'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-400 dark:hover:bg-gray-600'
            }`}
          >
            {t.name}
          </button>
        ))}
      </div>

      {/* Main Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        <div className="lg:col-span-3">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 overflow-hidden">
            <div className="p-4 border-b border-gray-100 dark:border-gray-700 flex items-center justify-between">
              <h2 className="font-semibold text-gray-800 dark:text-gray-100">Swarm Visualization</h2>
              <span className="text-xs text-gray-400 dark:text-gray-500">Drag nodes to rearrange &middot; Click to inspect</span>
            </div>
            <SwarmVisualization
              nodes={selectedTemplate.agents}
              links={selectedTemplate.connections.map((c) => ({ source: c.from, target: c.to, label: c.label }))}
              onNodeClick={handleNodeClick}
              className="h-[420px]"
            />
          </div>
        </div>

        <div className="space-y-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-4">
            <h3 className="font-semibold text-gray-800 dark:text-gray-100 mb-3">Agent Status</h3>
            <div className="space-y-2">
              {agentStatuses.map((agent) => (
                <div key={agent.id} className="flex items-center gap-3 p-2 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700">
                  <div
                    className="w-8 h-8 rounded-full flex items-center justify-center text-white text-xs font-bold flex-shrink-0"
                    style={{ backgroundColor: agent.color }}
                  >
                    {agent.name.split(/\s+/).filter(Boolean).map((w) => w[0]).join('')}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-700 dark:text-gray-300 truncate">{agent.name}</p>
                    <p className="text-xs text-gray-400 dark:text-gray-500">{agent.tasksCompleted} tasks</p>
                  </div>
                  <div className={`w-2 h-2 rounded-full flex-shrink-0 ${
                    agent.status === 'active' ? 'bg-green-500' :
                    agent.status === 'error' ? 'bg-red-500' : 'bg-gray-300 dark:bg-gray-600'
                  }`} />
                </div>
              ))}
            </div>
          </div>

          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-4">
            <h3 className="font-semibold text-gray-800 dark:text-gray-100 mb-3">Task Progress</h3>
            <div className="space-y-3">
              {[
                { label: 'Data Collection', progress: progress.data },
                { label: 'Processing', progress: progress.processing },
                { label: 'Output Generation', progress: progress.output },
              ].map((task) => (
                <div key={task.label}>
                  <div className="flex justify-between text-xs text-gray-500 dark:text-gray-400 mb-1">
                    <span>{task.label}</span>
                    <span>{task.progress}%</span>
                  </div>
                  <div className="h-2 bg-gray-100 dark:bg-gray-700 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-purple-500 rounded-full transition-all duration-300"
                      style={{ width: `${task.progress}%` }}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Communication Log */}
      <div ref={logRef} className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-4">
        <h3 className="font-semibold text-gray-800 dark:text-gray-100 mb-3">Communication Log</h3>
        <div className="bg-gray-900 rounded-lg p-3 h-48 overflow-y-auto font-mono text-xs">
          {logs.map((log, i) => (
            <div key={i} className={`${
              log.includes('[error]') ? 'text-red-400' :
              log.includes('[system]') ? 'text-yellow-400' :
              'text-green-400'
            }`}>{log}</div>
          ))}
          {swarmStatus === 'running' && (
            <div className="text-gray-500 animate-pulse">Processing...</div>
          )}
        </div>
      </div>

      {/* Error Display */}
      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl p-4">
          <h3 className="font-semibold text-red-700 dark:text-red-400 mb-1">Error</h3>
          <p className="text-sm text-red-600 dark:text-red-300">{error}</p>
        </div>
      )}

      {/* Real Output Display */}
      {showOutput && result && (
        <div className="space-y-4">
          {/* Web Sources */}
          {result.web_results && result.web_results.length > 0 && (
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-4">
              <h3 className="font-semibold text-gray-800 dark:text-gray-100 mb-3">Web Sources ({result.web_results.length})</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                {result.web_results.map((wr, i) => (
                  <a
                    key={i}
                    href={wr.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="p-3 rounded-lg border border-gray-100 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                  >
                    <p className="text-sm font-medium text-purple-600 dark:text-purple-400 truncate">{wr.title}</p>
                    <p className="text-xs text-gray-400 mt-1 line-clamp-2">{wr.snippet}</p>
                    <p className="text-xs text-gray-300 dark:text-gray-600 mt-1 truncate">{wr.url}</p>
                  </a>
                ))}
              </div>
            </div>
          )}

          {/* Full Research Output */}
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 p-6">
            <h3 className="font-semibold text-gray-800 dark:text-gray-100 mb-1">Research Results</h3>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
              Task: <span className="font-medium text-gray-800 dark:text-gray-200">{currentTask}</span>
            </p>

            {/* Agent-by-agent output */}
            {result.agent_outputs && result.agent_outputs.length > 0 && (
              <div className="space-y-4 mb-6">
                {result.agent_outputs.map((ao, i) => (
                  <div key={i} className="border-l-4 border-purple-500 pl-4">
                    <h4 className="text-sm font-semibold text-gray-800 dark:text-gray-200">{ao.agent}</h4>
                    <p className="text-xs text-gray-400 mb-2">Model: {ao.model}</p>
                    <div className="prose prose-sm dark:prose-invert max-w-none text-gray-700 dark:text-gray-300 leading-relaxed">
                      <Markdown remarkPlugins={[remarkGfm]}>{ao.message}</Markdown>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Combined reply */}
            {!result.agent_outputs && (
              <div className="prose prose-sm dark:prose-invert max-w-none text-gray-700 dark:text-gray-300 leading-relaxed">
                <Markdown remarkPlugins={[remarkGfm]}>{result.reply}</Markdown>
              </div>
            )}

            {/* Agents used */}
            <div className="mt-4 pt-4 border-t border-gray-100 dark:border-gray-700">
              <p className="text-xs text-gray-400">
                Agents used: {result.agents_used?.join(', ') || 'N/A'} — {result.agent_count} agents, {result.web_results?.length || 0} web sources
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
